import { MENTAL_HEALTH_ONLY_FIELD_PATHS } from "./filterKnownQuestionsBySpecialty";

export type KnownQuestionMatcher = {
  results_field_path: string;
  procedure_code?: string | null;
};

export type CustomTemplateQuestion = {
  question_text: string;
  category: string;
  expected_response_type: string;
  results_field_path?: string | null;
  procedure_code?: string | null;
};

export type CallScriptTemplateSpecialty =
  | "DENTAL"
  | "MEDICAL"
  | "MENTAL_HEALTH"
  | "MEDICAL_AND_DENTAL";

export type CallScriptTemplate = {
  id: string;
  label: string;
  knownQuestionMatchers: KnownQuestionMatcher[];
  customQuestions?: CustomTemplateQuestion[];
  /** When set, template button is shown only for these specialties. */
  specialties?: CallScriptTemplateSpecialty[];
};

const MAXIMUMS_MATCHERS: KnownQuestionMatcher[] = [
  { results_field_path: "maximums.individual_deductible" },
  { results_field_path: "maximums.individual_deductible_used" },
  { results_field_path: "maximums.individual_out_of_pocket_maximum" },
  { results_field_path: "maximums.individual_out_of_pocket_maximum_used" },
  { results_field_path: "maximums.family_deductible" },
  { results_field_path: "maximums.family_deductible_used" },
  { results_field_path: "maximums.family_out_of_pocket_maximum" },
  { results_field_path: "maximums.family_out_of_pocket_maximum_used" },
];

const PLAN_INFO_MATCHERS: KnownQuestionMatcher[] = [
  { results_field_path: "plan_information.is_active" },
  { results_field_path: "plan_information.effective_date" },
  { results_field_path: "plan_information.termination_date" },
  { results_field_path: "plan_information.is_calendar_year_plan" },
  { results_field_path: "plan_information.is_provider_in_network" },
  { results_field_path: "plan_information.is_primary_insurance" },
  { results_field_path: "plan_information.has_other_insurance" },
  { results_field_path: "plan_information.plan_type" },
  { results_field_path: "plan_information.plan_name" },
  { results_field_path: "plan_information.referral_required" },
  { results_field_path: "plan_information.referral_info" },
];

const XYZ_BENEFITS_MATCHERS: KnownQuestionMatcher[] = [
  { results_field_path: "procedure_codes.XYZ.is_covered", procedure_code: "XYZ" },
  { results_field_path: "procedure_codes.XYZ.copay_amount", procedure_code: "XYZ" },
  { results_field_path: "procedure_codes.XYZ.coinsurance_percentage", procedure_code: "XYZ" },
  { results_field_path: "procedure_codes.XYZ.is_deductible_waived", procedure_code: "XYZ" },
  { results_field_path: "procedure_codes.XYZ.frequency_limitations", procedure_code: "XYZ" },
  { results_field_path: "procedure_codes.XYZ.frequency_limitations_utilized", procedure_code: "XYZ" },
];

const XYZ_PRIOR_AUTH_MATCHERS: KnownQuestionMatcher[] = [
  { results_field_path: "procedure_codes.XYZ.is_prior_auth_required", procedure_code: "XYZ" },
  { results_field_path: "procedure_codes.XYZ.prior_auth_info", procedure_code: "XYZ" },
];

const XYZ_PROCEDURE_CODE_MATCHERS: KnownQuestionMatcher[] = [
  ...XYZ_BENEFITS_MATCHERS,
  ...XYZ_PRIOR_AUTH_MATCHERS,
];

/** Matches GracentPsychotherapyForm plan section (7 questions). */
const MENTAL_HEALTH_PLAN_MATCHERS: KnownQuestionMatcher[] = [
  { results_field_path: "plan_information.is_active" },
  { results_field_path: "plan_information.is_provider_in_network" },
  { results_field_path: "plan_information.is_primary_insurance" },
  { results_field_path: "plan_information.plan_type" },
  { results_field_path: "plan_information.effective_date" },
  { results_field_path: "plan_information.termination_date" },
  { results_field_path: "plan_information.is_calendar_year_plan" },
];

const MENTAL_HEALTH_EXTRA_MEDICAL_MATCHERS: KnownQuestionMatcher[] =
  MENTAL_HEALTH_ONLY_FIELD_PATHS.map((path) => ({
    results_field_path: path,
  }));

const CLAIMS_CUSTOM_QUESTIONS: CustomTemplateQuestion[] = [
  {
    question_text: "Is the claim found for the date of service provided?",
    category: "ClaimsInformation",
    expected_response_type: "boolean",
  },
  {
    question_text: "What is the status of the claim (denied, approved, or pending)?",
    category: "ClaimsInformation",
    expected_response_type: "string",
  },
  {
    question_text: "What is the claim number or ID?",
    category: "ClaimsInformation",
    expected_response_type: "string",
  },
  {
    question_text: "What date was the claim received?",
    category: "ClaimsInformation",
    expected_response_type: "date",
  },
  {
    question_text: "What date was it processed or last processed?",
    category: "ClaimsInformation",
    expected_response_type: "date",
  },
  {
    question_text: "If paid, on what date was it paid?",
    category: "ClaimsInformation",
    expected_response_type: "date",
  },
  {
    question_text: "If paid, how much did insurance pay?",
    category: "ClaimsInformation",
    expected_response_type: "string",
  },
  {
    question_text: "If denied, what is the reason for denial?",
    category: "ClaimsInformation",
    expected_response_type: "string",
  },
];

export const CALL_SCRIPT_TEMPLATES: CallScriptTemplate[] = [
  {
    id: "standard_benefits",
    label: "Benefits",
    knownQuestionMatchers: [
      ...MAXIMUMS_MATCHERS,
      ...PLAN_INFO_MATCHERS,
      ...XYZ_BENEFITS_MATCHERS,
    ],
  },
  {
    id: "prior_auth",
    label: "Prior Auth",
    knownQuestionMatchers: [
      ...MAXIMUMS_MATCHERS,
      ...PLAN_INFO_MATCHERS,
      ...XYZ_BENEFITS_MATCHERS,
      ...XYZ_PRIOR_AUTH_MATCHERS,
    ],
  },
  {
    id: "mental_health",
    label: "Mental Health",
    specialties: ["MENTAL_HEALTH"],
    knownQuestionMatchers: [
      ...MENTAL_HEALTH_PLAN_MATCHERS,
      ...MAXIMUMS_MATCHERS,
      ...XYZ_PROCEDURE_CODE_MATCHERS,
      ...MENTAL_HEALTH_EXTRA_MEDICAL_MATCHERS,
    ],
  },
  {
    id: "claims",
    label: "Claims",
    knownQuestionMatchers: [],
    customQuestions: CLAIMS_CUSTOM_QUESTIONS,
  },
];

export function filterCallScriptTemplatesBySpecialty(
  templates: CallScriptTemplate[],
  specialty: string | null | undefined,
): CallScriptTemplate[] {
  return templates.filter((template) => {
    if (!template.specialties) {
      return true;
    }
    return specialty === "MENTAL_HEALTH";
  });
}
